import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';
import ActionButton from '@/components/Button/ActionButton';
import { Card } from '@/components/Card/card';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import { canApply, ChunkResultPanel, currentPhaseKind, deriveImportStep, hasJobFailed, ImportProgress, ImportStepperHeader, isApplied, isImportConflictError, isImportNotFoundError, isJobSettled, useApplyImportSession, useImportJobDetail } from '@/features/people-csv-import';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
const CsvImportDetail = () => {
  const {
    importId
  } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    toast
  } = useToast();
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });
  const {
    data: job,
    error
  } = useImportJobDetail(importId);
  const {
    mutate: applySession,
    isPending: isApplying
  } = useApplyImportSession(importId);
  useEffect(() => {
    if (error != null && isImportNotFoundError(error)) {
      toast({
        description: t('errors.notFound'),
        variant: 'destructive'
      });
      navigate({
        to: '/person/csv-import',
        search: {}
      });
    }
  }, [error, navigate, toast, t]);
  const settled = job != null && isJobSettled(job);
  const failed = job != null && hasJobFailed(job);
  const currentStep = job != null ? deriveImportStep(job) : 1;

  // 表示対象フェーズ（validation = プレビュー / apply = 取り込み）
  const phaseKind = job != null ? currentPhaseKind(job) : 'validation';

  // 表示対象 chunk（現在フェーズ = validation / apply）
  const relevantChunks = useMemo(() => {
    if (job == null) return [];
    const wantKind = currentPhaseKind(job);
    const filtered = job.chunks.filter(c => c.kind === wantKind);
    return filtered.length > 0 ? filtered : job.chunks;
  }, [job]);

  // 動的キー（テンプレートリテラル）を避け、importType ごとに明示キーで解決する
  const unit = job != null ? match(job.importType).with('EMPLOYEE', () => t('detail.unit.employee')).with('CONTRACTOR', () => t('detail.unit.contractor')).with('BOARD_MEMBER', () => t('detail.unit.boardMember')).with('COLLABORATOR', () => t('detail.unit.collaborator')).exhaustive() : '';

  // パース自体の失敗（プレビューするセル結果が無い）
  const isParseLevelFailure = job?.failureReasonCode === 'csv_parse_failed' || job?.failureReasonCode === 'empty_csv_file';

  // 検証は完走したが有効行が 0（全行エラー）のケース
  const isAllInvalid = job != null && job.counts.validCount === 0 && (job.counts.invalidCount ?? 0) > 0;
  const failureMessage = job != null && failed ? match(job.failureReasonCode).with('csv_parse_failed', () => t('detail.failureReason.csvParseFailed')).with('empty_csv_file', () => t('detail.failureReason.emptyCsvFile')).otherwise(() => isAllInvalid ? t('detail.failureReason.noValidRows') : t('detail.failureReason.unknown')) : null;

  // chunk 結果が取得できるならプレビューを表示する（失敗時でも）。
  // ただしパース失敗はセル結果が無いため、失敗カードのみとする。
  const hasResults = job != null && settled && !isParseLevelFailure && relevantChunks.length > 0;
  const showApplyAction = job != null && canApply(job);
  const showCompleteAction = job != null && isApplied(job);
  const handleApply = () => {
    if (job == null) return;
    // 新しい importId は作らず、同じセッションに適用フェーズを開始する
    applySession(undefined, {
      onError: applyError => {
        toast({
          description: isImportConflictError(applyError) ? t('errors.conflict') : t('errors.enqueueFailed'),
          variant: 'destructive'
        });
      }
    });
  };
  return <VStack className="h-[calc(100vh-64px)] w-full items-start gap-6 overflow-hidden p-10">
      <VStack className="shrink-0 items-start gap-1">
        <Link to="/person/csv-import" search={{}}>
          <Text variant="labelReg" className="text-neutral-muted-on-fill">
            {t('breadcrumb')}
          </Text>
        </Link>
        {job != null && <Text variant="h3">{job.originalFileName}</Text>}
      </VStack>

      <ImportStepperHeader currentStep={currentStep} />

      <VStack className="min-h-0 w-full flex-1 items-start gap-6">
        {failureMessage != null && <Card className="w-full shrink-0 border-error-border bg-error-muted-fill p-6 text-error-on-muted-fill">
            <HStack className="items-center gap-2">
              <Icon iconName="AlertCircle" colorVariant="error" />
              <Text variant="pBold">{failureMessage}</Text>
            </HStack>
          </Card>}

        {/* 初期読み込み自体の失敗（notFound はトーストで離脱するため除外） */}
        {job == null && error != null && !isImportNotFoundError(error) && <Card className="w-full shrink-0 border-error-border bg-error-muted-fill p-6 text-error-on-muted-fill">
            <HStack className="items-center gap-2">
              <Icon iconName="AlertCircle" colorVariant="error" />
              <Text variant="pBold">{t('detail.failureReason.unknown')}</Text>
            </HStack>
          </Card>}

        {job != null && !settled && <ImportProgress counts={job.counts} phase={currentStep >= 4 ? 'apply' : 'validation'} />}

        {/* 失敗時でも chunk 結果があればプレビュー（セル単位エラー）を表示 */}
        {hasResults && job != null && <ChunkResultPanel importId={importId} chunks={relevantChunks} counts={job.counts} unit={unit} phase={phaseKind} />}
      </VStack>

      <Card className="w-full shrink-0">
        <HStack className="w-full items-center justify-between px-6 py-4">
          <Link to="/person/csv-import" search={{}}>
            <ActionButton variant="ghost" icon="ArrowLeftIcon">
              {t('detail.backToList')}
            </ActionButton>
          </Link>
          {showApplyAction && <ActionButton variant="default" icon="ArrowRightIcon" iconPosition="right" isLoading={isApplying} onClick={handleApply}>
              {t('detail.applyButton')}
            </ActionButton>}
          {showCompleteAction && <Link to="/people/workforce">
              <ActionButton variant="default">
                {tc('action.complete')}
              </ActionButton>
            </Link>}
        </HStack>
      </Card>
    </VStack>;
};
export const Route = createFileRoute('/_staff/person/csv-import/$importId')({
  component: CsvImportDetail
});